import { useContext, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import PermissionMatrixContext from "@/Contexts/PermissionMatrix/Context";
import { displayNotification } from "@/utils/notifications";

/**
 * Roles held by the selected employment. Roles are live references — the
 * grants they carry apply immediately and are read-only in the grant grid.
 */
const RolesStrip = () => {
  const {
    selectedEmploymentId,
    selectedAccess,
    companyRoles,
    rolesLoading,
    assignRoleTo,
    unassignRoleFrom,
  } = useContext(PermissionMatrixContext);

  const [busy, setBusy] = useState(false);

  const assignedRoles = selectedAccess?.roles ?? [];
  const assignedIds = useMemo(
    () => new Set(assignedRoles.map((r) => String(r.id))),
    [assignedRoles]
  );

  const assignableRoles = useMemo(
    () => companyRoles.filter((r) => !assignedIds.has(String(r.id))),
    [companyRoles, assignedIds]
  );

  if (!selectedEmploymentId) return null;

  const run = async (fn: () => Promise<void>, errorText: string) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      displayNotification({ type: "error", text: errorText });
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = (roleId: string) => {
    if (!roleId) return;
    run(
      () => assignRoleTo(selectedEmploymentId, roleId),
      "The role could not be assigned. Please try again."
    );
  };

  const handleUnassign = (roleId: string) => {
    run(
      () => unassignRoleFrom(selectedEmploymentId, roleId),
      "The role could not be removed. Please try again."
    );
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="subtitle2" fontWeight={800}>
        Roles
      </Typography>

      {assignedRoles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No roles assigned
        </Typography>
      ) : (
        assignedRoles.map((role) => (
          <Chip
            key={role.id}
            label={`${role.name} (${role.grants.length})`}
            onDelete={busy ? undefined : () => handleUnassign(String(role.id))}
            disabled={busy}
            size="small"
          />
        ))
      )}

      <Box sx={{ flex: 1 }} />

      {busy && <CircularProgress size={16} />}

      <Select
        size="small"
        displayEmpty
        value=""
        disabled={busy || rolesLoading || assignableRoles.length === 0}
        onChange={(event) => handleAssign(String(event.target.value))}
        sx={{ minWidth: 200 }}
        renderValue={() =>
          assignableRoles.length === 0 ? "All roles assigned" : "Assign a role…"
        }
      >
        {assignableRoles.map((role) => (
          <MenuItem key={role.id} value={String(role.id)}>
            {role.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default RolesStrip;
